import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { VoiceRing } from "../components/VoiceRing";
import { endpoints, type Colleague, type Me, type RoomRecording } from "../lib/api";
import {
  cannotRecord,
  keepScreenOn,
  levelMeter,
  openMicrophone,
  PieceUploader,
  recordingFormat,
  startRecorder,
} from "../lib/roomRecorder";

type Phase = "ready" | "starting" | "recording" | "paused" | "saving" | "saved";

/** Record an in-person meeting with this device's microphone: a ring of light
 * that moves with the voices, a timer, Pause and Stop. The audio goes to the
 * server in pieces while it records; Stop sends the last of it and queues the
 * transcript — the meeting then has a page like any other. */
export function RecordPage({ me }: { me: Me | null }) {
  const queryClient = useQueryClient();
  const csrfToken = me?.csrf_token ?? "";
  const [phase, setPhase] = useState<Phase>("ready");
  const [title, setTitle] = useState("");
  const [attendees, setAttendees] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [, rerender] = useState(0);
  const [saved, setSaved] = useState<string | null>(null);
  const [noWakeLock, setNoWakeLock] = useState(false);

  const session = useRef<RoomRecording | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const meter = useRef<ReturnType<typeof levelMeter> | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const uploader = useRef<PieceUploader | null>(null);
  const wakeLock = useRef<{ release: () => void } | null>(null);
  const clock = useRef({ before: 0, since: 0 }); // seconds recorded before this stretch, and when it began
  const stoppingByHand = useRef(false);

  const unfinished = useQuery({ queryKey: ["unfinished-recordings"], queryFn: endpoints.unfinishedRecordings });
  const level = useCallback(() => meter.current?.level() ?? 0, []);
  const seconds = () => clock.current.before + (clock.current.since ? (Date.now() - clock.current.since) / 1000 : 0);

  // The timer, and the stop at the length limit.
  useEffect(() => {
    if (phase !== "recording") return;
    const tick = setInterval(() => {
      const now = seconds();
      setElapsed(now);
      if (session.current && now >= session.current.max_seconds) {
        setNotice("Reached the longest a recording can run, so it was stopped and saved.");
        void stop();
      }
    }, 250);
    return () => clearInterval(tick);
  });

  // Closing the page mid-recording loses the last few seconds: ask first.
  useEffect(() => {
    if (phase !== "recording" && phase !== "paused" && phase !== "saving") return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  // A phone gives the screen lock back when the page is hidden; take it again.
  useEffect(() => {
    if (phase !== "recording") return;
    const onVisible = async () => {
      if (document.visibilityState === "visible" && !wakeLock.current) wakeLock.current = await keepScreenOn();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [phase]);

  function release() {
    stream.current?.getTracks().forEach((track) => track.stop());
    meter.current?.close();
    wakeLock.current?.release();
    stream.current = meter.current = wakeLock.current = null;
  }

  async function start() {
    const blocked = cannotRecord();
    if (blocked) return setError(blocked);
    setError(null);
    setNotice(null);
    setPhase("starting");
    try {
      stream.current = await openMicrophone();
      const mime = recordingFormat();
      session.current = await endpoints.startRecording(
        { title: title.trim(), attendees, mime: mime || "audio/webm" }, csrfToken);
      uploader.current = new PieceUploader(session.current.session_id, csrfToken, () => rerender((n) => n + 1));
      meter.current = levelMeter(stream.current);
      stoppingByHand.current = false;
      recorder.current = startRecorder(stream.current, mime, (piece) => uploader.current?.add(piece));
      // The phone stopped it — the screen locked, another app took the
      // microphone: keep what's there.
      recorder.current.addEventListener("stop", () => {
        if (!stoppingByHand.current) {
          setNotice("The device stopped the recording (the screen may have locked). What was recorded is being saved.");
          void save();
        }
      });
      clock.current = { before: 0, since: Date.now() };
      wakeLock.current = await keepScreenOn();
      setNoWakeLock(!wakeLock.current);
      setPhase("recording");
    } catch (failure) {
      release();
      setError((failure as Error).message);
      setPhase("ready");
    }
  }

  function pause() {
    recorder.current?.pause();
    clock.current = { before: seconds(), since: 0 };
    setElapsed(clock.current.before);
    setPhase("paused");
  }

  function resume() {
    recorder.current?.resume();
    clock.current = { ...clock.current, since: Date.now() };
    setPhase("recording");
  }

  async function stop() {
    const active = recorder.current;
    if (!active || active.state === "inactive") return;
    stoppingByHand.current = true;
    // The last piece arrives with the stop.
    await new Promise<void>((resolve) => {
      active.addEventListener("stop", () => resolve(), { once: true });
      active.stop();
    });
    await save();
  }

  async function save() {
    const current = session.current;
    if (!current) return;
    const duration = seconds();
    clock.current = { before: duration, since: 0 };
    setElapsed(duration);
    release();
    setPhase("saving");
    await uploader.current?.flushed();
    try {
      await endpoints.finishRecording(current.session_id, { duration_seconds: duration, attendees }, csrfToken);
      setSaved(current.session_id);
      setPhase("saved");
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
    } catch (failure) {
      setError(`The audio is on the server, but it couldn't be finished: ${(failure as Error).message}. ` +
        "It's listed below as unfinished; finish it from there.");
      setPhase("ready");
      unfinished.refetch();
    }
  }

  function again() {
    session.current = recorder.current = uploader.current = null;
    clock.current = { before: 0, since: 0 };
    setElapsed(0);
    setTitle("");
    setSaved(null);
    setNotice(null);
    setPhase("ready");
    unfinished.refetch();
  }

  const finishOld = useMutation({
    mutationFn: (old: { session_id: string; seconds: number }) =>
      endpoints.finishRecording(old.session_id, { duration_seconds: old.seconds, attendees: [] }, csrfToken),
    onSuccess: () => {
      unfinished.refetch();
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
  });

  const deleteOld = useMutation({
    mutationFn: (sessionId: string) => endpoints.deleteRecording(sessionId, csrfToken),
    onSuccess: () => {
      unfinished.refetch();
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
  });

  const live = phase === "recording";
  const busy = phase === "starting" || phase === "saving";
  const sending = uploader.current?.pending ?? 0;
  const problem = uploader.current?.problem;
  const status = {
    ready: "Ready when you are",
    starting: "Opening the microphone…",
    recording: "Recording",
    paused: "Paused",
    saving: sending ? `Saving the last of it… (${sending} to send)` : "Saving…",
    saved: "Saved",
  }[phase];

  return (
    <div className="page-body record-page">
      <header className="record-head">
        <h1>Record an in-person meeting</h1>
        <p>This device's microphone records the room. Stop when you're done: the transcript, speaker names and
          minutes appear on the meeting's page a few minutes later.</p>
      </header>

      {(phase === "ready" || phase === "starting") && (
        <section className="record-setup">
          <label className="record-field">
            <span>Title</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={120}
                   placeholder="e.g. Design review, room 2" />
          </label>
          <WhoIsHere me={me} chosen={attendees} onChange={setAttendees} />
        </section>
      )}

      <section className={`record-stage${live ? " live" : ""}`}>
        <div className="record-ring">
          <VoiceRing level={level} active={live} />
          <div className="record-centre">
            <span className="record-time">{clockText(elapsed)}</span>
            <span className={`record-status ${phase}`}>
              {live && <span className="record-dot" aria-hidden />}
              {status}
            </span>
          </div>
        </div>

        <div className="record-controls">
          {(phase === "ready" || phase === "starting") && (
            <button className="record-main" onClick={start} disabled={busy || !csrfToken}>
              <IconMic /> {phase === "starting" ? "Starting…" : "Start recording"}
            </button>
          )}
          {(phase === "recording" || phase === "paused") && (
            <>
              {phase === "recording" ? (
                <button className="record-round" onClick={pause} aria-label="Pause"><IconPause /></button>
              ) : (
                <button className="record-round" onClick={resume} aria-label="Resume"><IconMic /></button>
              )}
              <button className="record-main stop" onClick={() => void stop()}>
                <IconStop /> Stop and save
              </button>
            </>
          )}
          {phase === "saved" && saved && (
            <>
              <Link className="record-main" to={`/meetings/${saved}`}>Open the meeting</Link>
              <button className="record-round wide" onClick={again}>Record another</button>
            </>
          )}
        </div>

        {phase === "saved" && (
          <p className="record-note">Saved. It's being transcribed now — the transcript, names and minutes appear on its
            page in a few minutes.</p>
        )}
        {(live || phase === "paused") && (
          <p className="record-note">
            {noWakeLock ? "Keep this page open and the screen on: " : "Keep this page open: "}
            a phone can stop the microphone when it locks.
            {sending > 1 && ` ${sending} pieces waiting to send.`}
          </p>
        )}
        {problem && phase !== "saved" && <p className="record-note warn">Retrying: {problem}</p>}
        {notice && <p className="record-note warn">{notice}</p>}
        {error && <div className="error">{error}</div>}
      </section>

      {phase === "ready" && (unfinished.data?.length ?? 0) > 0 && (
        <section className="card record-unfinished">
          <h2 className="card-title">Unfinished recordings</h2>
          <p className="doc-note">These were started but never stopped — the page closed, or the device died.
            What arrived can still be saved.</p>
          <ul>
            {unfinished.data!.map((old) => (
              <li key={old.session_id}>
                <span>
                  <strong>{old.title}</strong>
                  <small>{new Date(old.started_at).toLocaleString()} · {clockText(old.seconds)} received</small>
                </span>
                <span className="record-unfinished-actions">
                  <button
                    className="btn-quiet danger"
                    disabled={deleteOld.isPending || finishOld.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete “${old.title}”? The ${clockText(old.seconds)} of audio that arrived is `
                        + "deleted for good.")) deleteOld.mutate(old.session_id);
                    }}
                  >
                    Delete
                  </button>
                  <button className="btn-secondary" disabled={finishOld.isPending || deleteOld.isPending}
                          onClick={() => finishOld.mutate(old)}>
                    Save it
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {finishOld.error && <div className="error">{(finishOld.error as Error).message}</div>}
          {deleteOld.error && <div className="error">{(deleteOld.error as Error).message}</div>}
        </section>
      )}
    </div>
  );
}

/** Who's in the room, picked from colleagues who use the app: they get the
 * recording, and their names help tell the voices apart. */
function WhoIsHere({ me, chosen, onChange }: { me: Me | null; chosen: string[]; onChange: (emails: string[]) => void }) {
  const colleagues = useQuery({ queryKey: ["colleagues"], queryFn: endpoints.colleagues, staleTime: 5 * 60_000 });
  const [typed, setTyped] = useState("");
  const names = useMemo(() => new Map((colleagues.data ?? []).map((c) => [c.email, c.name ?? c.email])), [colleagues.data]);
  const needle = typed.trim().toLowerCase();
  const matches = needle
    ? (colleagues.data ?? []).filter((c: Colleague) => c.email !== me?.email.toLowerCase() && !chosen.includes(c.email)
      && (c.email.includes(needle) || (c.name ?? "").toLowerCase().includes(needle))).slice(0, 6)
    : [];
  const add = (email: string) => {
    onChange([...chosen, email]);
    setTyped("");
  };
  return (
    <div className="record-field">
      <span>Who's here <small>(optional)</small></span>
      <div className="record-who">
        {chosen.map((email) => (
          <span key={email} className="share-chip">
            {names.get(email) ?? email}
            <button onClick={() => onChange(chosen.filter((e) => e !== email))} aria-label={`Remove ${email}`}>✕</button>
          </span>
        ))}
        <input value={typed} onChange={(event) => setTyped(event.target.value)} placeholder="Add a colleague"
               onKeyDown={(event) => {
                 if (event.key === "Enter" && matches[0]) {
                   event.preventDefault();
                   add(matches[0].email);
                 }
               }} />
      </div>
      {matches.length > 0 && (
        <ul className="share-suggestions record-suggestions">
          {matches.map((person) => (
            <li key={person.email}>
              <button onMouseDown={(event) => {
                event.preventDefault();
                add(person.email);
              }}>
                <span><strong>{person.name ?? person.email}</strong>{person.name && <small>{person.email}</small>}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function clockText(totalSeconds: number): string {
  const s = Math.floor(totalSeconds);
  const hours = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return hours ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
export function IconMic() {
  return <Svg><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0014 0M12 18v3" /></Svg>;
}
function IconPause() {
  return <Svg><path d="M9 6v12M15 6v12" /></Svg>;
}
function IconStop() {
  return <Svg><rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" stroke="none" /></Svg>;
}
